/**
 * ============================================================================
 * GET METRICS API — accessor for POST /getMetrics.
 * ============================================================================
 *
 * LAYER: accessor. Owns the TRANSPORT-level pagination protocol: the backend
 * returns a paginationKey when more pages exist; a complete result requires
 * following it until absent. Callers (the RTK endpoint) always receive the
 * FULL accumulated dataset — partial data is never returned (any page failure
 * throws; spec R5.3).
 *
 * SCOPE: this accessor fetches every page for the request scope it is given,
 * serially (page N+1 needs page N's token). It does NOT parallelise weeks —
 * weeks are separate cache entries, and useMetricsWindow dispatches one query
 * per week, so the parallelism lives there. Keeping that decision in one place
 * avoids two competing notions of "how many weeks at once".
 *
 * Cache policy (keys, retention) is NOT here — that belongs to the RTK
 * endpoint (features/api/metricsApi.ts).
 */

import { ReportingApiClient, ReportingApiError, ErrorCode } from 'src/accessors/ReportingApiClient';
import { GetMetricsInput, GetMetricsOutput, DcrSnapshotData } from 'src/types/metricsApi.types';

/**
 * Follow-the-key page limit, per requested week. Backend pages are coarse
 * (hundreds of DCRs); exceeding this indicates a runaway key loop, not real
 * data.
 */
export const MAX_METRICS_PAGES = 50;

/**
 * Merge one getMetrics page into the accumulator. Pages may re-send a period
 * with additional DCRs — dcrData concatenates per period, first-seen period
 * order is preserved. Exported for unit tests.
 */
export function mergeMetricsPage(
  merged: Map<string, DcrSnapshotData>,
  order: string[],
  page: GetMetricsOutput,
): void {
  for (const period of page.data ?? []) {
    const existing = merged.get(period.reportingPeriod);
    if (existing) {
      existing.dcrData = existing.dcrData.concat(period.dcrData);
    } else {
      merged.set(period.reportingPeriod, { ...period, dcrData: [...period.dcrData] });
      order.push(period.reportingPeriod);
    }
  }
}

class GetMetricsApiClient extends ReportingApiClient {
  /** One page, exactly as the backend returns it. */
  public async getMetricsPage(input: GetMetricsInput): Promise<GetMetricsOutput> {
    return this.post<GetMetricsOutput>('/getMetrics', input);
  }

  /**
   * The complete dataset for the requested scope: follows paginationKey until
   * exhausted, merging dcrData per period. Throws ReportingApiError on any page
   * failure or when the page cap is exceeded — callers never see a partially
   * loaded result.
   */
  public async getMetricsAllPages(input: GetMetricsInput): Promise<GetMetricsOutput> {
    const merged = new Map<string, DcrSnapshotData>();
    const order: string[] = [];
    let paginationKey: string | undefined;

    for (let pageNo = 0; pageNo < MAX_METRICS_PAGES; pageNo++) {
      const body: GetMetricsInput = paginationKey ? { ...input, paginationKey } : input;
      const page = await this.getMetricsPage(body);
      mergeMetricsPage(merged, order, page);
      paginationKey = page.paginationKey;
      if (!paginationKey) {
        // Envelope fields are identical across a scope's pages.
        return {
          data: order.map((reportingPeriod) => merged.get(reportingPeriod)!),
          dashboardId: page.dashboardId,
          reviewType: page.reviewType,
          dataMode: page.dataMode,
          lastRefreshedAt: page.lastRefreshedAt,
        };
      }
    }

    throw new ReportingApiError(
      500,
      ErrorCode.InternalFailure,
      `getMetrics pagination did not terminate within ${MAX_METRICS_PAGES} pages`,
    );
  }
}

/** Singleton (portal convention): one shared instance app-wide. */
export const GetMetricsApi = new GetMetricsApiClient();
